import { STORAGE_KEYS } from '../../utils/constants';

/**
 * Serviço para gerenciar localStorage de forma segura
 */
class LocalStorageService {
  /**
   * Salva um item no localStorage
   * @param {string} key - Chave
   * @param {any} value - Valor (será convertido para JSON)
   */
  setItem(key, value) {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Erro ao salvar no localStorage:', error);
    }
  }

  /**
   * Recupera um item do localStorage
   * @param {string} key - Chave
   * @returns {any} - Valor deserializado ou null
   */
  getItem(key) {
    try {
      const serializedValue = localStorage.getItem(key);
      if (serializedValue === null) {
        return null;
      }
      return JSON.parse(serializedValue);
    } catch (error) {
      console.error('Erro ao recuperar do localStorage:', error);
      return null;
    }
  }

  /**
   * Remove um item do localStorage
   * @param {string} key - Chave
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Erro ao remover do localStorage:', error);
    }
  }

  /**
   * Limpa todo o localStorage
   */
  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Erro ao limpar localStorage:', error);
    }
  }

  /**
   * Verifica se uma chave existe
   * @param {string} key - Chave
   * @returns {boolean}
   */
  hasItem(key) {
    return localStorage.getItem(key) !== null;
  }

  // Métodos específicos para auth
  
  /**
   * Salva token de autenticação
   * @param {string} token
   */
  setAuthToken(token) {
    this.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  /**
   * Recupera token de autenticação
   * @returns {string|null}
   */
  getAuthToken() {
    return this.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Remove token de autenticação
   */
  removeAuthToken() {
    this.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Salva refresh token
   * @param {string} token
   */
  setRefreshToken(token) {
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Recupera refresh token
   * @returns {string|null}
   */
  getRefreshToken() {
    return this.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Remove refresh token
   */
  removeRefreshToken() {
    this.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Salva dados do usuário
   * @param {Object} userData
   */
  setUserData(userData) {
    this.setItem(STORAGE_KEYS.USER_DATA, userData);
  }

  /**
   * Recupera dados do usuário
   * @returns {Object|null}
   */
  getUserData() {
    return this.getItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Remove dados do usuário
   */
  removeUserData() {
    this.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Limpa todos os dados de autenticação
   */
  clearAuth() {
    this.removeAuthToken();
    this.removeRefreshToken();
    this.removeUserData();
  }

  /**
   * Verifica se usuário está autenticado
   * @returns {boolean}
   */
  isAuthenticated() {
    return this.hasItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Salva tema
   * @param {string} theme - 'light' ou 'dark'
   */
  setTheme(theme) {
    this.setItem(STORAGE_KEYS.THEME, theme);
  }

  /**
   * Recupera tema
   * @returns {string|null}
   */
  getTheme() {
    return this.getItem(STORAGE_KEYS.THEME);
  }
}

// Exporta instância única (Singleton)
const localStorageService = new LocalStorageService();
export default localStorageService;